import type { LightingStyle } from './graphics-options';

// ── Camera frustum ratios ────────────────────────────────────────────
export const CAMERA_FRUSTUM_RATIO_WIDE = 0.75;
export const CAMERA_FRUSTUM_RATIO_MEDIUM = 0.5;
export const CAMERA_FRUSTUM_RATIO_NARROW = 0.25;

// ── Lighting presets ──────────────────────────────────────────────────
// Camera-attached key + fill DirectionalLights, plus a low world-anchored
// HemisphereLight and the shared PMREM environment's contribution. Applied by
// Engine.applyLightingStyle(); see also Controls.createLightingRig().
export interface LightingPresetValues {
  keyIntensity: number;
  fillIntensity: number;
  hemiIntensity: number;
  /** Scene.environmentIntensity — how strongly the shared PMREM env lights materials. */
  environmentIntensity: number;
}

export const LIGHTING_PRESETS: Readonly<Record<LightingStyle, LightingPresetValues>> = {
  // Strong key/fill contrast (key well above fill/hemi/env so shape reads from
  // shading, not colour) -- an Onshape/Fusion-like readable "product shot".
  studio: { keyIntensity: 7.5, fillIntensity: 0.35, hemiIntensity: 0.15, environmentIntensity: 0.18 },
  // Gentler version: key still leads, but fill/hemi/env are much closer to it.
  soft: { keyIntensity: 2.4, fillIntensity: 1.4, hemiIntensity: 0.9, environmentIntensity: 0.7 },
  // Close to the old flat hemisphere+ambient wash, but tone-mapped so nothing clips.
  flat: { keyIntensity: 0.2, fillIntensity: 0.15, hemiIntensity: 2.8, environmentIntensity: 1.2 }
};

// ── Material defaults (input parts, PerFaceMaterial) ───────────────────
export const PART_METALNESS = 0.15;
export const PART_ROUGHNESS = 0.45;
export const PART_REFLECTIVITY = 0.5;
export const PART_CLEARCOAT = 0;
export const PART_ENVMAP_INTENSITY = 0.55;

// ── Material defaults (optimized meshes) ─────────────────────────────
export const OPTIMIZED_METALNESS = 0.15;
export const OPTIMIZED_REFLECTIVITY = 0.5;
export const OPTIMIZED_ROUGHNESS = 0.45;
export const OPTIMIZED_CLEARCOAT = 0;
/** Unused while OPTIMIZED_CLEARCOAT is 0; kept for a future "Glossy" preset. */
export const OPTIMIZED_CLEARCOAT_ROUGHNESS = 0.1;
export const OPTIMIZED_ENVMAP_INTENSITY = 0.45;
export const POLYGON_OFFSET_FACTOR = -2;
/**
 * Polygon offset on part and optimized-mesh faces. Pushes the faces back so
 * the edge lines drawn on them always win the depth test; the lines' own
 * factor-only offset is zero on faces that face the camera.
 */
export const FACE_POLYGON_OFFSET = { polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 } as const;

// ── Edge lines (CSS pixels; converted to buffer pixels by the engine) ──
export const WIRE_LINE_WIDTH_CSS_PX = 1;
export const WIRE_SELECTED_LINE_WIDTH_CSS_PX = 2.5;

/**
 * How far input part faces are pushed back, as a fraction of the model's
 * largest dimension, while an optimized/result overlay that repeats them is
 * visible. Well above the tessellation difference between the two meshes and
 * well below any real wall thickness.
 */
export const OVERLAY_DEPTH_BIAS_FRACTION = 1e-3;

// ── Geometry processing ──────────────────────────────────────────────
export const BVH_TARGET_LEAF_SIZE = 10;
export const WIRE_CUTOFF_ANGLE = 20;
export const GENERATED_SHAPE_CUTOFF_ANGLE = 45;

/**
 * Triangle budget for the main-thread weld + edge-split fallback (used only when
 * the geometry worker is unavailable or has failed). Deliberately lower than the
 * worker's own budget: three's mergeVertices/EdgeSplitModifier are string-keyed
 * and would block the UI thread outright on a large imported mesh.
 */
export const MAIN_THREAD_MESH_PROCESSING_MAX_TRIANGLES = 500_000;

/**
 * Triangle budget for the soft-jaws result's feature-edge overlay.
 *
 * EdgesGeometry walks every triangle pair on the main thread and emits a line
 * segment per qualifying edge, so on an imported mesh — which carries no CAD
 * wires and therefore always got the overlay — it costs more than the mesh
 * itself and yields a wireframe too dense to read anyway.
 */
export const FEATURE_EDGES_MAX_TRIANGLES = 500_000;

// ── Load indicator sphere ────────────────────────────────────────────
export const LOAD_SPHERE_RADIUS = 2;
export const LOAD_SPHERE_WIDTH_SEGMENTS = 32;
export const LOAD_SPHERE_HEIGHT_SEGMENTS = 16;

// ── Load indicator auto-fit (scales arrow & sphere to assembly bbox) ─
// Indicators are originally created at LOAD_INDICATOR_DEFAULT_LENGTH so
// proportions (sphere radius : arrow length) stay anchored to that value.
export const LOAD_INDICATOR_SCALE_FACTOR = 0.5;
export const LOAD_INDICATOR_DEFAULT_LENGTH = 40;
export const LOAD_INDICATOR_BBOX_EPSILON = 1e-6;

// ── FPS monitor ──────────────────────────────────────────────────────
export const FPS_THRESHOLD = 15;
export const FPS_CHECK_INTERVAL_MS = 1000;
export const FPS_STABILITY_DELAY_MS = 3000;

// ── Animation ────────────────────────────────────────────────────────
export const TEXTURE_ANIMATION_INTERVAL_MS = 1000 / 60;

// ── Render scheduling ────────────────────────────────────────────────
/** How long the loop keeps drawing every frame after the last sign of activity. */
/** Device pixel ratio the canvas renders at, at most; sharper than this costs fill rate for no visible gain. */
export const MAX_PIXEL_RATIO = 2;

export const RENDER_ACTIVE_WINDOW_MS = 500;
/** How often an idle scene is still redrawn, for a change nothing announced. */
export const IDLE_RENDER_INTERVAL_MS = 500;

// ── Scene defaults ──────────────────────────────────────────────────
export const DEFAULT_EMPTY_SIZE = 100;

// ── Coordinate system triads ──────────────────────────────────────────
/** Registry key for the UCS tab's live preview triad, shared with the tests that assert it. */
export const UCS_PREVIEW_TRIAD_KEY = 'ucs-preview';

/** The origin dot of a frame triad, named so callers can find it among the axes. */
export const TRIAD_ORIGIN_MARKER_NAME = 'triad-origin';

/** Colours of a frame triad's x, y and z arms, the order the global axis helper uses. */
export const FRAME_AXIS_COLORS: readonly [number, number, number] = [0xd62728, 0x2ca02c, 0x1f77b4];

/** Letters at the arm tips: plain for the global axes, primed for a local frame. */
export const GLOBAL_AXIS_LABELS: readonly [string, string, string] = ['X', 'Y', 'Z'];
export const LOCAL_AXIS_LABELS: readonly [string, string, string] = ['X\u2032', 'Y\u2032', 'Z\u2032'];

/**
 * How faint an unlocked constraint axis is drawn. Kept visible enough to read
 * the frame's orientation, faint enough not to be mistaken for a lock.
 */
export const CONSTRAINT_FREE_OPACITY = 0.4;

/** Part paint per load-case role, shared by the part colour and its focus label. */
export const PART_BOUNDARY_CONDITION_COLOR = 0xff4500;
export const PART_LOAD_COLOR = 0x32cd32;

/** The marker colours the annotation size preview copies. */
export const PART_FORCE_ARROW_COLOR = 0x0000ff;
export const PART_MOMENT_ARROW_COLOR = 0xff0000;

// ── Overlay colours (roles mapped in overlay-style.ts OVERLAY_COLORS) ─
export const REMOTE_POINT_SPHERE_COLOR = 0xff0000;
export const MASS_SPHERE_COLOR = 0x779ecb;
export const REFERENCE_POINT_COLOR = 0xe6a23c;
export const LINK_LINE_COLOR = 0x000000;
export const GROUP_EDIT_CONTROL_COLOR = 0x000000;
export const REACTION_FORCE_ARROW_COLOR = 0xff8800;
export const DRAW_DIRECTION_ARROW_COLOR = 0xff0000;
export const OPEN_DIRECTION_ARROW_COLOR = 0x00bcd4;
export const BUILD_DIRECTION_ARROW_COLOR = 0x1e88e5;
export const MAX_STRESS_MARKER_COLOR = 0xff0040;
export const CONSTRAINT_DEFAULT_COLOR = 0x6d6d6d;
export const CENTER_OF_GRAVITY_TARGET_COLOR = 0x9c27b0;

/** How solid the sample markers are, so they read as a preview. */
export const ANNOTATION_PREVIEW_OPACITY = 0.55;

/**
 * Turns each sample away from the viewer so it reads as a solid rather than
 * flat-on: the triad shows all three arms, and the moment arc is not edge-on.
 */
export const ANNOTATION_PREVIEW_TILT: [number, number, number] = [-Math.PI / 3, 0, -Math.PI / 4];

/** Auto-save hold reasons for dragging a load's remote point and its reference point. */
export const REMOTE_POINT_DRAG_HOLD = 'remote-point-drag';
export const REFERENCE_POINT_DRAG_HOLD = 'reference-point-drag';
/** Auto-save hold reason for dragging the center of gravity target. */
export const CENTER_OF_GRAVITY_DRAG_HOLD = 'center-of-gravity-drag';
